import { storageGet, storageSet, storageDel, storageKeys, encodeKey } from './storage';
import { fileSaveContent } from './api';

/**
 * Client-side file-level outbox — the local write-ahead cache backing
 * offline edits, draft recovery, and the "unsynced" UI badges.
 *
 * Entries are `dirty`, `syncing`, or `conflict`; there is no `clean` state —
 * a successful push to the server marks `syncing` (in-flight to Git) and the
 * foreground status poll resolves it to `synced` (delete) or `conflict`.
 * Each entry:
 *
 *     { filePath, content, baseContent, baseSha, state, remoteSha, remoteContent }
 *
 * `content`     — latest local full-file content.
 * `baseContent` — last successfully synced full-file content (badge diff base).
 * `baseSha`     — last successfully synced git blob sha (concurrency token).
 * `state`       — "dirty" | "syncing" | "conflict".
 * `remoteSha` / `remoteContent` — (conflict only) snapshot of the server's
 *     current state from the 409 response, for the future conflict-resolve
 *     feature (three-way input).
 *
 * The sync loop is a module-level background task; it cannot reach React
 * state, so it publishes results through an internal pub/sub (`subscribe`).
 */

const DRAFT_KEY = '::draft::';

// ── Per-user namespace ────────────────────────────────────────────
// Outbox entries are keyed by file path only, so without a namespace a
// leftover entry from one account would be read (and re-pushed) by the next
// account to sign in on the same device.  Prefix every key with the current
// user's id.  encodeKey() output never contains `:` (it is percent-encoded),
// so `uid:` is an unambiguous, collision-free prefix.
function _currentUserId() {
  try {
    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.uid) return String(payload.uid);
      if (payload.sub) return String(payload.sub);
    }
  } catch { /* malformed token — fall through */ }
  return localStorage.getItem('user_id') || localStorage.getItem('username') || 'anon';
}

function _nsKey(rawPath) {
  return `${_currentUserId()}:${encodeKey(rawPath)}`;
}

// ── Pub/sub (internal, browser-memory only) ───────────────────────

const _listeners = new Set();

/** Subscribe to outbox events.  Returns an unsubscribe function. */
export function subscribe(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

function _emit(payload) {
  for (const l of [..._listeners]) {
    try {
      l(payload);
    } catch {
      /* a listener crash must never break the sync loop */
    }
  }
}

// ── Outbox CRUD ────────────────────────────────────────────────────

/** Read an outbox entry for *filePath*, or `null`. */
export async function outboxGet(filePath) {
  const v = await storageGet(_nsKey(filePath));
  return v && typeof v === 'object' && v.filePath ? v : null;
}

/** Upsert an outbox entry.  Stamps `updatedAt` for oldest-first ordering. */
export async function outboxPut(filePath, entry) {
  await storageSet(_nsKey(filePath), { ...entry, filePath, updatedAt: Date.now() });
  _emit({ type: 'changed', path: filePath });
}

/** Delete an outbox entry (no-op if absent). */
export async function outboxRemove(filePath) {
  await storageDel(_nsKey(filePath));
  _emit({ type: 'changed', path: filePath });
}

/** List every outbox entry, oldest first. */
export async function outboxList() {
  const prefix = `${_currentUserId()}:`;
  const draftKey = _nsKey(DRAFT_KEY);
  const keys = await storageKeys();
  const entries = [];
  for (const k of keys) {
    if (!k.startsWith(prefix)) continue; // another account's entries
    if (k === draftKey) continue;
    const v = await storageGet(k);
    if (v && typeof v === 'object' && v.filePath) entries.push(v);
  }
  entries.sort((a, b) => (a.updatedAt || 0) - (b.updatedAt || 0));
  return entries;
}

/** Count of outbox entries (dirty + conflict). */
export async function outboxCount() {
  return (await outboxList()).length;
}

/** True if any entry is dirty/syncing/conflict (i.e. there is unsynced content). */
export async function outboxHasPending() {
  const list = await outboxList();
  return list.some((e) => e.state === 'dirty' || e.state === 'syncing' || e.state === 'conflict');
}

/** Wipe the entire outbox (used on account switch / explicit discard). */
export async function outboxClear() {
  const prefix = `${_currentUserId()}:`;
  const keys = await storageKeys();
  await Promise.all(keys.filter((k) => k.startsWith(prefix)).map((k) => storageDel(k)));
  _emit({ type: 'changed' });
}

/** The local read view: return the entry if it's dirty/syncing/conflict/local-only, else null. */
export async function outboxGetForRead(filePath) {
  const e = await outboxGet(filePath);
  if (e && (e.state === 'dirty' || e.state === 'syncing' || e.state === 'conflict' || e.state === 'local-only')) return e;
  return null;
}

// ── Draft (replaces the legacy `block_save_draft` localStorage key) ──

export async function draftGet() {
  const v = await storageGet(_nsKey(DRAFT_KEY));
  return v && typeof v === 'object' ? v : null;
}

export async function draftSet(draft) {
  await storageSet(_nsKey(DRAFT_KEY), draft);
}

export async function draftClear() {
  await storageDel(_nsKey(DRAFT_KEY));
}

// ── Recovery flag (gates draft recovery to session-expiry only) ──
// The editor draft is written on every keystroke, so its mere presence is not
// a reliable "recover me" signal — a normal finished edit also leaves a draft
// behind whenever the clear doesn't land.  This flag is set only when the
// session actually expires (api.js redirectToLogin) and consumed/cleared when
// the recovered edit is resolved.  Node.jsx recovers a draft only when this
// flag is set, so a stale leftover draft never pops on a plain app open.

const RECOVERY_KEY = '::recovery::';

export async function recoveryFlagSet() {
  await storageSet(_nsKey(RECOVERY_KEY), { pending: true, timestamp: Date.now() });
}

export async function recoveryFlagGet() {
  const v = await storageGet(_nsKey(RECOVERY_KEY));
  return v && typeof v === 'object' && v.pending ? v : null;
}

export async function recoveryFlagClear() {
  await storageDel(_nsKey(RECOVERY_KEY));
}

// ── Draft flush coordination (for the session-expired confirm dialog) ──

let _draftFlusher = null;

/** Register the currently-open block editor's immediate draft writer. */
export function registerDraftFlusher(fn) {
  _draftFlusher = fn;
}

/** Await the registered draft writer, if any. */
export async function flushDraft() {
  if (_draftFlusher) {
    try {
      await _draftFlusher();
    } catch {
      /* draft write is best-effort */
    }
  }
}

// ── Write-ahead save ───────────────────────────────────────────────

/**
 * Persist an edit locally (write-ahead), then trigger the sync loop.
 *
 * `baseContent` / `baseSha` are only used when the file has no existing
 * entry — they capture the last-synced state so the first edit can diff
 * against it.  Subsequent edits preserve the original base.
 *
 * @returns the resulting outbox entry.
 */
export async function saveFileLocal(path, newContent, baseContent, baseSha, timeline = null, baseUnknown = false) {
  const existing = await outboxGet(path);
  let entry;
  let wasConflict = false;
  if (existing) {
    entry = existing;
    wasConflict = entry.state === 'conflict';
  } else {
    entry = {
      baseContent: baseContent ?? newContent,
      baseSha: baseSha ?? null,
      timeline: [],
    };
  }
  entry.content = newContent;
  // A base-unknown edit (read failed at load time) is parked locally and must
  // NOT auto-push — the backend would receive a null sha and overwrite the
  // unseen remote content.  It stays `local-only` until reconciled.
  entry.baseUnknown = baseUnknown || entry.baseUnknown || false;
  if (entry.baseUnknown) {
    entry.state = 'local-only';
  } else {
    // A conflicted file stays parked — further edits update the local content
    // but must NOT flip it back to `dirty`, which would auto-retry against a
    // stale baseSha and loop 409.  Resolution is a separate feature.
    entry.state = wasConflict ? 'conflict' : 'dirty';
    if (!wasConflict) {
      delete entry.remoteSha;
      delete entry.remoteContent;
    }
  }
  // Timeline marks accumulate as an array.  A mark action APPENDS new
  // entries; a normal edit (timeline === null) PRESERVES already-pending
  // marks.  The array is only drained once the backend ACKs the save.
  const pending = Array.isArray(entry.timeline) ? entry.timeline : [];
  if (timeline && Array.isArray(timeline) && timeline.length > 0) {
    entry.timeline = [...pending, ...timeline];
  } else {
    entry.timeline = pending;
  }
  await outboxPut(path, entry);
  if (!wasConflict && !entry.baseUnknown) runSync();
  return entry;
}

// ── Sync loop (single-flight, drain-until-empty, backoff) ─────────

let _syncing = false;
let _retryAfter = 0;
let _backoff = 0;
let _retryTimer = null;

// Consecutive non-409 save failures.  After the cap we stop auto-retrying
// (leaving entries `dirty`) instead of looping forever against a
// persistently-failing backend — the next explicit save or reconnect resumes.
const _MAX_CONSECUTIVE_FAILURES = 5;
// Long backoff once the backend reports a rate-limit (429/503): give Gitee's
// rate-limit window time to expire instead of hammering it and keeping it 403.
const _RATE_LIMIT_BACKOFF_MS = 5 * 60 * 1000;
let _failureStreak = 0;
let _rateLimited = false;

/** Schedule a one-shot sync retry after `delay` ms (cancels any prior). */
function _scheduleRetry(delay) {
  _clearRetry();
  _retryTimer = setTimeout(() => {
    _retryTimer = null;
    runSync().catch(() => {});
  }, delay);
}

/** Cancel any pending retry timer. */
function _clearRetry() {
  if (_retryTimer !== null) {
    clearTimeout(_retryTimer);
    _retryTimer = null;
  }
}

/** True if the device believes it's offline. */
function _isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Drain dirty entries to the backend.  Single-flight — concurrent calls
 * while a run is in progress return immediately (the in-progress loop picks
 * up any newly written dirty entries on its next iteration).
 */
export async function runSync() {
  if (_syncing) return;
  if (_isOffline()) return;
  if (Date.now() < _retryAfter) return;

  _syncing = true;
  try {
    await _drain();
  } finally {
    _syncing = false;
  }
}

async function _drain() {
  while (true) {
    const entry = await _nextDirty();
    if (!entry) {
      _clearRetry();
      break;
    }

    const path = entry.filePath;
    const snapshot = { content: entry.content, baseSha: entry.baseSha, timeline: entry.timeline };

    let result;
    try {
      result = await fileSaveContent(path, entry.content, entry.baseSha, null, false, entry.timeline || null);
    } catch (err) {
      if (err && err.status === 409) {
        await _stageConflict(path, err);
        _backoff = 0;
        _failureStreak = 0;
        continue; // leave this entry parked; keep draining others
      }

      const rateLimited = !!(err && (
        err.status === 429 ||
        err.status === 503 ||
        (err.detail && err.detail.retryable)
      ));
      _failureStreak += 1;

      if (rateLimited || _rateLimited) {
        // Backend is rate-limiting (or was last run) — back off for a long
        // window and surface it, instead of retrying on a 60s loop that keeps
        // Gitee 403-ing.
        _rateLimited = true;
        _backoff = _RATE_LIMIT_BACKOFF_MS;
        _retryAfter = Date.now() + _backoff;
        _emit({ type: 'rate-limited' });
        _scheduleRetry(_backoff);
        break;
      }

      if (_failureStreak >= _MAX_CONSECUTIVE_FAILURES) {
        // Persistent failure (e.g. auth 502) — stop auto-retrying so we don't
        // loop forever.  Entries stay `dirty`; the next edit / reconnect
        // (resetSyncBackoff) restarts the loop.
        _emit({ type: 'paused', reason: 'too many failures' });
        break; // no reschedule
      }

      // Network / other error — keep dirty, back off, stop this run.
      _backoff = Math.min(_backoff === 0 ? 5000 : _backoff * 2, 60000);
      _retryAfter = Date.now() + _backoff;
      _scheduleRetry(_backoff);
      break;
    }

    _backoff = 0;
    _failureStreak = 0;
    _rateLimited = false;
    const newSha = result.sha || null;
    const cur = await outboxGet(path);
    // Marks handed off to the backend (the server ACKed this POST) — drop
    // them from the front of the pending array.  Any marks appended while the
    // POST was in flight remain (they also changed the content).
    const sentCount = Array.isArray(snapshot.timeline) ? snapshot.timeline.length : 0;

    if (cur && cur.content === snapshot.content && cur.baseSha === snapshot.baseSha) {
      // Unchanged during the POST — the server ACKed it; now it is in-flight
      // to Git.  Mark `syncing` (do NOT delete) — the foreground status poll
      // resolves it to `synced` (delete) or `conflict` (external edit).
      cur.baseContent = snapshot.content;
      cur.baseSha = newSha;
      cur.state = 'syncing';
      cur.timeline = Array.isArray(cur.timeline) ? cur.timeline.slice(sentCount) : [];
      await storageSet(_nsKey(path), { ...cur, updatedAt: Date.now() });
      _emit({ type: 'synced', path, sha: newSha, content: snapshot.content, stillDirty: true });
    } else if (cur) {
      // Edited mid-POST — advance the base, stay dirty (avoid a spurious 409).
      cur.baseContent = snapshot.content;
      cur.baseSha = newSha;
      cur.timeline = Array.isArray(cur.timeline) ? cur.timeline.slice(sentCount) : [];
      await storageSet(_nsKey(path), { ...cur, updatedAt: Date.now() });
      _emit({ type: 'synced', path, sha: newSha, content: snapshot.content, stillDirty: true });
    } else {
      _emit({ type: 'synced', path, sha: newSha, content: snapshot.content, stillDirty: false });
    }
  }
}

async function _nextDirty() {
  const list = await outboxList();
  return list.find((e) => e.state === 'dirty') || null;
}

async function _stageConflict(path, err) {
  const detail = err && err.detail && typeof err.detail === 'object' ? err.detail : {};
  const cur = await outboxGet(path);
  if (!cur) return;
  cur.state = 'conflict';
  cur.remoteSha = detail.current_sha ?? null;
  cur.remoteContent = detail.current_content ?? null;
  await storageSet(_nsKey(path), { ...cur, updatedAt: Date.now() });
  _emit({ type: 'changed', path, state: 'conflict' });
}

/**
 * Apply a batch status result from the backend to `syncing` outbox entries.
 *
 * `synced`   → the server pushed to Git; drop the local entry.
 * `conflict` → the server detected an external edit; that conflict is now
 *              tracked server-side, so drop the local entry — it will surface
 *              via `GET /file/conflicts` and resolve through the conflict view.
 * `syncing`  → still in-flight; leave the entry as-is.
 *
 * @param {Array<{path: string, state: string}>} statusItems
 */
export async function applyFileStatuses(statusItems) {
  for (const item of statusItems || []) {
    if (item.state !== 'synced' && item.state !== 'conflict') continue;
    const cur = await outboxGet(item.path);
    if (!cur || cur.state !== 'syncing') continue;
    await outboxRemove(item.path);
  }
}

/** Reset the sync backoff (e.g. on `navigator.onLine` recovery). */
export function resetSyncBackoff() {
  _backoff = 0;
  _retryAfter = 0;
  _failureStreak = 0;
  _rateLimited = false;
  _clearRetry();
}
